import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MatchCard from '@/components/feature/MatchCard';
import { leagueMeta, leagueOrder, searchMatches, setPageMeta } from '@/lib/site';

const suggestions = ['湖人', '勇士', 'CBA', '辽宁', '国际赛', '中国男篮'];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get('q')?.trim() || '';
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = searchMatches(keyword);

  useEffect(() => {
    if (keyword) {
      setPageMeta(
        `“${keyword}”搜索结果 - 篮球直播`,
        `篮球直播中与“${keyword}”相关的赛事共 ${results.length} 场，包含球队对阵、开赛时间与直播状态，点击即可查看赛事详情。`,
        `${keyword}直播,${keyword}赛程,篮球直播,篮球比赛`,
      );
    } else {
      setPageMeta(
        '赛事搜索 - 篮球直播',
        '在篮球直播搜索 NBA、CBA 与国际篮球赛事，支持球队名称、赛事名称与联赛名称关键词检索。',
        '篮球直播搜索,NBA搜索,CBA搜索,篮球赛事',
      );
    }
  }, [keyword, results.length]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (value) setParams({ q: value });
    else setParams({});
  };

  return (
    <div className="w-full">
      <section className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '赛事搜索' }]} />
          <h1 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-950">赛事搜索</h1>
          <p className="mt-1.5 text-sm text-foreground-600">支持球队名称、赛事名称与联赛名称关键词检索</p>

          <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="flex flex-1 items-center gap-2 rounded-md bg-background-100 border border-background-200 px-3.5 py-2.5">
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：湖人、勇士、CBA、国际赛"
                aria-label="搜索赛事关键词"
                name="q"
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput('')}
                  aria-label="清空关键词"
                  className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-circle-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索赛事
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">热门搜索</span>
            {suggestions.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setParams({ q: item })}
                className="rounded-full bg-background-100 border border-background-200 px-2.5 py-1 text-[11px] text-foreground-700 hover:border-accent-300 hover:text-accent-700 transition-colors cursor-pointer"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-sm text-foreground-700">
            {keyword ? (
              <>
                “<span className="text-accent-700 font-medium">{keyword}</span>” 的搜索结果，
                共 <span className="text-foreground-950 font-medium">{results.length}</span> 场赛事
              </>
            ) : (
              '输入关键词开始搜索'
            )}
          </h2>
          {keyword && (
            <Link to="/" className="text-xs text-primary-700 hover:text-primary-900 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          )}
        </div>

        {results.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
            {results.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-background-50 border border-dashed border-background-300 py-14 px-6 text-center">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-2xl">
              <i className="ri-search-eye-line" />
            </span>
            <h3 className="mt-3 text-base text-foreground-900">
              {keyword ? `没有找到与“${keyword}”相关的赛事` : '请输入赛事或球队关键词'}
            </h3>
            <p className="mt-1.5 text-sm text-foreground-500">
              {keyword ? '换个关键词试试，或从下面的联赛入口浏览全部赛事' : '例如输入“湖人”“CBA”“国际赛”等'}
            </p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-xl mx-auto">
              {leagueOrder.map((key) => (
                <Link
                  key={key}
                  to={`/category/${key}`}
                  className="flex items-center justify-center gap-2 rounded-md bg-background-100 border border-background-200 py-2.5 text-xs text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors"
                >
                  <i className={leagueMeta[key].icon} />
                  {leagueMeta[key].name} 赛事直播
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}